<script setup>
import { defineProps, defineEmits, toRef } from 'vue';
import { useDataTable } from '@/composables/table/useDataTable.js'; // Adjust path as needed
import { ArrowUpIcon, ArrowDownIcon, ArrowUpDownIcon } from 'lucide-vue-next';
// Import your UI components
import Checkbox from '@/components/ui/checkbox/Checkbox.vue'; // Placeholder
import Button from '@/components/ui/button/Button.vue'; // Placeholder
import Input from '@/components/ui/input/Input.vue';
import { ItemGroup, Item, ItemTitle, ItemDescription } from '../../ui/item/index.js'
import UmisisDropdownMenu from './UmisisDropdownMenu.vue'
import { Badge } from '../../ui/badge/index.js'
import { DropdownMenuItem } from '../../ui/dropdown-menu/index.js'
import InputText from '../form/inputs/InputText.vue' // Placeholder

// 1. Define ALL props for the component, including selection props
const props = defineProps({
  rows: {
    type: Array,
    default: () => []
  },
  fields: {
    type: Array,
    default: () => []
  },
  orderBy: {
    type: String,
    default: null
  },
  orderDirection: {
    type: String,
    default: 'asc'
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingRowCount: {
    type: Number,
    default: 10,
  },
  filters: {
    type: Object,
    default: () => ({})
  },
  rowClass: {
    type: Function,
    default: () => (row) => ''
  },
  // Add the selection props
  selectable: {
    type: Boolean,
    default: false
  },
  selectedRows: {
    type: Array,
    default: () => [],
  },
  cards: {
    type: Boolean,
    default: false
  }

});

// 2. Define ALL emits, including the selection emit
const emit = defineEmits(['sort-changed', 'filters-changed', 'update:selectedRows','search-changed']);

// 3. Instantiate the composable
// We pass all the prop Refs and the emit function it needs.
const {
  fieldsSearchable,
  fieldsSortable,
  sortChanged,
  searchChanged,
  filterChanged,
  rowFieldValue,
  selectedRowsLocal, // You can use this for debugging
  toggleRowSelection,
  isRowSelected,
  // selectAll, // Available if you add a "select all" checkbox
  // clearSelection, // Available if you need it
} = useDataTable({
  rows: toRef(props, 'rows'),
  fields: toRef(props, 'fields'),
  orderBy: toRef(props, 'orderBy'),
  orderDirection: toRef(props, 'orderDirection'),
  filters: toRef(props, 'filters'),
  selectable: toRef(props, 'selectable'),
  selectedRows: toRef(props, 'selectedRows'),
  emit,
});
const searchQuery = ref('');
// The script block is now done. All logic is in the composable.
// The template below will work as-is because we've returned
// all the same-named functions and computed properties.
</script>

<template>
  <div class="flex flex-row gap-2">
    <InputText v-if="fieldsSearchable.length > 0" v-model="searchQuery" placeholder="Search and hit ENTER..." @keyup.enter="searchChanged(searchQuery)" />
    <UmisisDropdownMenu v-if="fieldsSortable.length > 0" variant="outline" class="float-right">
      <template #button-content>
        Sort by <Badge v-if="orderBy">
          {{ orderBy }} <arrow-down-icon v-if="orderDirection === 'desc'" /><arrow-up-icon v-else />
        </Badge>
      </template>
      <DropdownMenuItem v-for="fieldSortable in fieldsSortable" :key="'sort-field-' + fieldSortable.name" @click="sortChanged(fieldSortable.name, orderBy === fieldSortable.name && orderDirection === 'asc' ? 'desc' : 'asc')">
        {{ fieldSortable.label }}
      </DropdownMenuItem>
    </UmisisDropdownMenu>
  </div>
  <div class="clear-both" />
  <ItemGroup v-if="loading">
    <Item v-for="n in loadingRowCount" :key="'loading-row-' + n" class="h-10">
      <div class="h-6 bg-gray-300 rounded w-full animate-pulse" />
    </Item>
  </ItemGroup>
  <ItemGroup v-else>
    <Item v-for="(row, rowIndex) in rows" :key="'table-row-key-' + rowIndex" :class="[cards && 'p-4 border rounded-lg shadow-sm flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4','hover:bg-gray-100', rowClass(row),{'bg-gray-200': isRowSelected(row, 'id')}]">
      <slot name="item" :row="row">
        <ItemTitle v-if="$slots.title">
          <slot name="title" :row="row" :index="rowIndex">
            {{ row[fields[1].name] }}
          </slot>
        </ItemTitle>

        <ItemDescription v-if="$slots.description">
          <slot name="description" :row="row" :index="rowIndex" />
        </ItemDescription>
      </slot>
    </Item>
  </ItemGroup>
</template>
